import { z } from "zod";
import { idSchema, localeSchema } from "./common.js";
import { tenantStatusSchema, userRoleSchema } from "./tenant.js";

/**
 * Password policy: length is the only hard rule; strength comes from length, and a breach check
 * (k-anonymity lookup) is added before beta.
 */
export const passwordSchema = z.string().min(10).max(200);

export const signupRequestSchema = z.object({
  tenantName: z.string().trim().min(1).max(200),
  displayName: z.string().trim().min(1).max(200),
  email: z.email().trim().toLowerCase(),
  password: passwordSchema,
  locale: localeSchema.default("en"),
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

export const loginRequestSchema = z.object({
  email: z.email().trim().toLowerCase(),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const authUserSchema = z.object({
  id: idSchema,
  email: z.email(),
  displayName: z.string(),
  role: userRoleSchema,
  locale: localeSchema,
  /** False until the verification link was opened; the web client shows a reminder meanwhile. */
  emailVerified: z.boolean(),
});
export type AuthUser = z.infer<typeof authUserSchema>;

export const authTenantSchema = z.object({
  id: idSchema,
  name: z.string(),
  status: tenantStatusSchema,
});
export type AuthTenant = z.infer<typeof authTenantSchema>;

export const authMfaSchema = z.object({
  /** True when the user has a confirmed second factor. */
  enabled: z.boolean(),
  /** True when this session still has to pass the second factor before other endpoints open up. */
  pending: z.boolean(),
});
export type AuthMfa = z.infer<typeof authMfaSchema>;

export const authSessionSchema = z.object({
  user: authUserSchema,
  tenant: authTenantSchema,
  session: z.object({
    id: idSchema,
    expiresAt: z.iso.datetime(),
    /** Sent back as the x-csrf-token header on every state-changing request of this session. */
    csrfToken: z.string(),
  }),
  mfa: authMfaSchema,
});
export type AuthSession = z.infer<typeof authSessionSchema>;

export const sessionSummarySchema = z.object({
  id: idSchema,
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  current: z.boolean(),
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

export const sessionListSchema = z.object({
  sessions: z.array(sessionSummarySchema),
});
export type SessionList = z.infer<typeof sessionListSchema>;

// --- Invitations ---------------------------------------------------------------------------------

/** Roles an administrator may hand out; ownership is transferred, never invited. */
export const inviteRoleSchema = userRoleSchema.exclude(["owner"]);
export type InviteRole = z.infer<typeof inviteRoleSchema>;

export const inviteCreateRequestSchema = z.object({
  email: z.email().trim().toLowerCase(),
  role: inviteRoleSchema.default("user"),
  locale: localeSchema.optional(),
});
export type InviteCreateRequest = z.infer<typeof inviteCreateRequestSchema>;

export const inviteSummarySchema = z.object({
  id: idSchema,
  email: z.email(),
  role: inviteRoleSchema,
  locale: localeSchema,
  invitedBy: z.object({ id: idSchema, displayName: z.string() }),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
});
export type InviteSummary = z.infer<typeof inviteSummarySchema>;

export const inviteCreatedSchema = z.object({
  invite: inviteSummarySchema,
  /** True when the invitation email was handed to the mailer; false means share the link by hand. */
  emailSent: z.boolean(),
  /** Returned once so an administrator can pass it on when email did not arrive; never stored in clear. */
  acceptUrl: z.url(),
});
export type InviteCreated = z.infer<typeof inviteCreatedSchema>;

export const inviteListSchema = z.object({ invites: z.array(inviteSummarySchema) });
export type InviteList = z.infer<typeof inviteListSchema>;

export const invitePreviewSchema = z.object({
  tenantName: z.string(),
  email: z.email(),
  role: inviteRoleSchema,
  expiresAt: z.iso.datetime(),
});
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

export const inviteAcceptRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(200),
  password: passwordSchema,
});
export type InviteAcceptRequest = z.infer<typeof inviteAcceptRequestSchema>;

// --- TOTP ----------------------------------------------------------------------------------------

export const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Enter the 6-digit code");
export const recoveryCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]{5}-[a-z0-9]{5}$/, "Recovery codes look like xxxxx-xxxxx");

export const totpEnrollResponseSchema = z.object({
  /** Base32 secret for manual entry. */
  secret: z.string(),
  /** otpauth:// URI for QR codes. */
  otpauthUri: z.string(),
});
export type TotpEnrollResponse = z.infer<typeof totpEnrollResponseSchema>;

export const totpConfirmRequestSchema = z.object({ code: totpCodeSchema });
export const totpConfirmResponseSchema = z.object({
  /** Shown once; each code works a single time. */
  recoveryCodes: z.array(recoveryCodeSchema),
});
export type TotpConfirmResponse = z.infer<typeof totpConfirmResponseSchema>;

export const mfaVerifyRequestSchema = z
  .object({
    code: totpCodeSchema.optional(),
    recoveryCode: recoveryCodeSchema.optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: "Provide either a code or a recovery code",
  });
export type MfaVerifyRequest = z.infer<typeof mfaVerifyRequestSchema>;

export const totpDisableRequestSchema = z.object({
  password: z.string().min(1).max(200),
  code: totpCodeSchema,
});
export type TotpDisableRequest = z.infer<typeof totpDisableRequestSchema>;

// --- Passkeys (WebAuthn) -------------------------------------------------------------------------

/**
 * WebAuthn option and response payloads follow the W3C JSON shapes; they are validated in depth by
 * the WebAuthn library, so the contracts only pin the fields the API itself relies on.
 */
export const webauthnOptionsSchema = z.looseObject({ challenge: z.string() });
export type WebauthnOptions = z.infer<typeof webauthnOptionsSchema>;

export const webauthnResponseSchema = z.looseObject({
  id: z.string().min(1),
  rawId: z.string().min(1),
  type: z.literal("public-key"),
  response: z.looseObject({ clientDataJSON: z.string().min(1) }),
});
export type WebauthnResponse = z.infer<typeof webauthnResponseSchema>;

export const passkeyNameSchema = z.string().trim().min(1).max(100);

export const passkeyRegisterVerifyRequestSchema = z.object({
  name: passkeyNameSchema.default("Passkey"),
  response: webauthnResponseSchema,
});
export type PasskeyRegisterVerifyRequest = z.infer<typeof passkeyRegisterVerifyRequestSchema>;

export const passkeyLoginVerifyRequestSchema = z.object({ response: webauthnResponseSchema });
export type PasskeyLoginVerifyRequest = z.infer<typeof passkeyLoginVerifyRequestSchema>;

export const passkeySummarySchema = z.object({
  id: idSchema,
  name: passkeyNameSchema,
  /** multiDevice passkeys sync through a platform account; singleDevice ones live on one authenticator. */
  deviceType: z.enum(["singleDevice", "multiDevice"]),
  backedUp: z.boolean(),
  transports: z.array(z.string()),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type PasskeySummary = z.infer<typeof passkeySummarySchema>;

export const passkeyListSchema = z.object({ passkeys: z.array(passkeySummarySchema) });
export type PasskeyList = z.infer<typeof passkeyListSchema>;

// --- Email verification and password reset ------------------------------------------------------

export const emailVerifyRequestSchema = z.object({ token: z.string().min(20).max(200) });
export type EmailVerifyRequest = z.infer<typeof emailVerifyRequestSchema>;

export const passwordForgotRequestSchema = z.object({ email: z.email().trim().toLowerCase() });
export type PasswordForgotRequest = z.infer<typeof passwordForgotRequestSchema>;

export const passwordResetRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordChangeRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});
export type PasswordChangeRequest = z.infer<typeof passwordChangeRequestSchema>;

// --- Workspace members (admin console) ------------------------------------------------------------

export const userSummarySchema = z.object({
  id: idSchema,
  email: z.email(),
  displayName: z.string(),
  role: userRoleSchema,
  locale: localeSchema,
  emailVerified: z.boolean(),
  /** True when a TOTP authenticator is confirmed; passkeys are listed per user separately. */
  mfaEnabled: z.boolean(),
  suspended: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type UserSummary = z.infer<typeof userSummarySchema>;

export const userListSchema = z.object({ users: z.array(userSummarySchema) });
export type UserList = z.infer<typeof userListSchema>;

export const userRoleChangeRequestSchema = z.object({ role: userRoleSchema });
export type UserRoleChangeRequest = z.infer<typeof userRoleChangeRequestSchema>;
